import { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ChevronDown, FileText, Loader2, MessageSquare, RefreshCw } from 'lucide-react';
import AccessDenied from '../AccessDenied/AccessDenied';
import CommentCard from './CommentCard';
import CommentComposer from './CommentComposer';
import useForumThread, { DEFAULT_PAGE_SIZE } from './useForumThread';
import useMediaQuery from '../../../core/hooks/useMediaQuery';
import { useProfileStore } from '../../../core/store/useProfileStore';
import { ADMIN, PAID, hasProfile, type Profile } from '../../auth/accessPolicy';
import type { CommentSort } from '../../../sdk/@types/Forum';

const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(' ');

const SORTS: Array<{ value: CommentSort; label: string }> = [
  { value: 'useful', label: 'Mais úteis' },
  { value: 'recent', label: 'Recentes' },
  { value: 'staff', label: 'Professores' },
];

export interface QuestionForumProps {
  questionId?: string;
  /**
   * A questão já foi respondida (ou o gabarito revelado). Enquanto for
   * `false` nada é renderizado e **nenhuma chamada de fórum acontece**.
   */
  answered: boolean;
  /** "Questão 1" — contexto no cabeçalho do overlay mobile. */
  questionLabel?: string;
  /** "Deserção — art. 188 do CPM" — linha de contexto do overlay mobile. */
  questionContext?: string;
  /** Força a apresentação. Sem isso, decide pela largura da janela. */
  mobile?: boolean;
  /** Comentários de topo na primeira página. */
  pageSize?: number;
  /** Nome do usuário logado — iniciais do avatar do composer. */
  currentUserName?: string;
  /** Perfil; sem isso vem do `useProfileStore`. */
  profile?: Profile | null;
  /** Navegação SPA para os links da negativa de assinatura. */
  onNavigate?: (to: string) => void;
  className?: string;
}

/**
 * Fórum da questão — a thread inteira, do cabeçalho ao "Ver mais N".
 *
 * Monta de duas formas, e é ele quem decide qual: embutido abaixo da
 * justificativa no desktop, e no mobile um ponto de entrada que abre uma página
 * inteira em overlay (mesmo padrão do leitor de texto base, `SharedTextPanel`),
 * porque o carrossel não tem URL por questão (decisão 2 do design). Quem monta
 * o componente — hoje o `CarrosselForm` — não precisa saber de nada disso.
 *
 * Enquanto as rotas do fórum não existirem no Go API, a resposta é 404 e a
 * seção degrada para o painel de erro/negativa padrão. Isso é de propósito: o
 * carrossel continua funcionando sem o fórum.
 */
export default function QuestionForum({
  questionId,
  answered,
  questionLabel,
  questionContext,
  mobile,
  pageSize = DEFAULT_PAGE_SIZE,
  currentUserName,
  profile: profileProp,
  onNavigate,
  className,
}: QuestionForumProps) {
  const headingId = useId();
  const storeProfile = useProfileStore((state) => state.profile);
  const status = useProfileStore((state) => state.status);
  const load = useProfileStore((state) => state.load);

  // `/carrossel` é só `ProtectedRoute` — não passa por `RequireProfile`, que é
  // quem normalmente aquece o cache. A semente do localStorage costuma bastar;
  // esta chamada cobre a sessão que caiu direto aqui. É deduplicada no store.
  useEffect(() => {
    if (profileProp === undefined && status === 'idle') load();
  }, [profileProp, status, load]);

  const profile = profileProp === undefined ? storeProfile : profileProp;
  const staff = hasProfile(profile, ADMIN);
  const canParticipate = hasProfile(profile, PAID);

  const narrow = useMediaQuery('(max-width: 767px)');
  const isMobile = mobile ?? narrow;
  const [open, setOpen] = useState(false);

  const thread = useForumThread({ questionId, answered, pageSize, staff });
  const { comments, approvedCount, remaining, loading, error } = thread;

  // Esc fecha o overlay — segunda saída além do "Ver questão".
  useEffect(() => {
    if (!open) return;
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  // Trocar de questão fecha o overlay: o conteúdo dele já não é o mesmo.
  useEffect(() => setOpen(false), [questionId]);

  // Decisão 4: sem resposta, sem seção e sem requisição.
  if (!answered || !questionId) return null;

  const sortTabs = (
    <div
      role="group"
      aria-label="Ordenar comentários"
      className="flex flex-wrap items-center gap-1.5"
    >
      {SORTS.map(({ value, label }) => {
        const active = thread.sort === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            onClick={() => thread.setSort(value)}
            className={cx(
              'rounded-chip border px-3 py-1 text-xs font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60',
              'focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
              active
                ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                : 'border-edge bg-surface text-ink-secondary hover:border-edge-strong hover:bg-surface-hover hover:text-ink',
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const composer = (
    <CommentComposer
      canParticipate={canParticipate}
      authorName={currentUserName}
      // Admin/master publica direto — o aviso de revisão prévia seria mentira.
      showModerationNote={!staff}
      onNavigate={onNavigate}
      onSubmit={(text) => thread.submit(text)}
    />
  );

  /**
   * A thread sem o composer.
   *
   * Os dois frames colocam o composer em lugares diferentes: no desktop
   * (`tcgeJ`) ele abre a seção, logo abaixo do cabeçalho; no overlay mobile
   * (`y6od9`/`UwKNg`) ele é uma barra fixa no rodapé, com a lista rolando por
   * cima. Por isso a lista é montada à parte e cada apresentação a posiciona.
   */
  const thread$ = (
    <>
      {loading && (
        <p role="status" className="m-0 inline-flex items-center gap-2 text-xs text-ink-muted">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          Carregando comentários…
        </p>
      )}

      {!loading &&
        error &&
        (error.kind ? (
          <AccessDenied
            kind={error.kind}
            variant="panel"
            detail={error.raw}
            onNavigate={onNavigate}
          />
        ) : (
          <div
            role="status"
            className="flex flex-col items-center gap-3 rounded-card border border-edge bg-surface px-6 py-8 text-center"
          >
            <p className="m-0 text-sm font-semibold text-ink">Não foi possível carregar o fórum</p>
            <p className="m-0 text-xs leading-relaxed text-ink-secondary">
              O fórum desta questão está indisponível no momento. A questão continua normal.
            </p>
            <button
              type="button"
              onClick={thread.reload}
              className="inline-flex items-center gap-2 rounded-input border border-edge bg-surface px-3 py-1.5 text-xs font-semibold text-ink-secondary transition-colors hover:border-edge-strong hover:bg-surface-hover hover:text-ink"
            >
              <RefreshCw size={13} aria-hidden />
              Tentar novamente
            </button>
          </div>
        ))}

      {!loading && !error && comments.length === 0 && (
        <div className="flex flex-col items-center gap-1 rounded-card border border-dashed border-edge px-6 py-8 text-center">
          <p className="m-0 text-sm font-semibold text-ink">Ninguém comentou esta questão ainda</p>
          <p className="m-0 text-xs leading-relaxed text-ink-secondary">
            {canParticipate
              ? 'Seja o primeiro a comentar: uma dica, um macete ou a dúvida que ficou.'
              : 'Assine para ser o primeiro a comentar esta questão.'}
          </p>
        </div>
      )}

      {!loading && !error && comments.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-5 p-0">
          {comments.map((comment) => (
            <CommentCard
              key={comment.id}
              comment={comment}
              canParticipate={canParticipate}
              mostUsefulId={thread.mostUsefulId}
              onVote={thread.vote}
              onFlag={thread.flag}
              onReply={(parentId, text) => thread.submit(text, parentId)}
              onEdit={thread.edit}
            />
          ))}
        </ul>
      )}

      {!loading && !error && remaining > 0 && (
        <button
          type="button"
          onClick={thread.expand}
          className="inline-flex items-center justify-center gap-2 rounded-input border border-edge bg-surface px-4 py-2 text-xs font-semibold text-ink-secondary transition-colors hover:border-edge-strong hover:bg-surface-hover hover:text-ink"
        >
          Ver mais {remaining} {remaining === 1 ? 'comentário' : 'comentários'}
          <ChevronDown size={14} aria-hidden />
        </button>
      )}
    </>
  );

  /* ---------------------------------------------------------------- mobile */

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cx(
            'flex w-full items-center gap-3 rounded-card border border-edge bg-surface px-4 py-3 text-left',
            'transition-colors hover:border-edge-strong hover:bg-surface-hover',
            className,
          )}
        >
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-accent-blue/[0.14] text-accent-blue-ink"
          >
            <MessageSquare size={18} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-bold text-ink">Fórum da questão</span>
            <span className="block text-xs text-ink-secondary">
              {approvedCount === 1 ? '1 comentário' : `${approvedCount} comentários`}
            </span>
          </span>
          <ChevronDown size={16} className="-rotate-90 text-ink-muted" aria-hidden />
        </button>

        {open &&
          createPortal(
            <div
              data-testid="forum-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby={headingId}
              className="fixed inset-0 z-[1000] flex h-[100dvh] w-full flex-col bg-canvas"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              <div className="flex items-center gap-3 border-b border-edge px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Fechar o fórum"
                  className="shrink-0 rounded p-1 text-ink-secondary transition-colors hover:bg-surface-hover hover:text-ink"
                >
                  <ArrowLeft size={18} aria-hidden />
                </button>
                <div className="min-w-0 flex-1 text-center">
                  <h2 id={headingId} className="m-0 truncate text-sm font-bold text-ink">
                    Fórum da questão
                    <span className="sr-only"> · {approvedCount} comentários</span>
                  </h2>
                  <p className="m-0 truncate text-xs text-ink-muted">
                    {[questionLabel, `${approvedCount} comentários`].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <span className="w-7 shrink-0" aria-hidden />
              </div>

              <div className="flex items-center gap-2 border-b border-edge px-4 py-2">
                <FileText size={14} className="shrink-0 text-ink-muted" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-xs text-ink-secondary">
                  {questionContext ?? questionLabel ?? 'Questão do carrossel'}
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="shrink-0 rounded text-xs font-semibold text-brand"
                >
                  Ver questão
                </button>
              </div>

              <div className="border-b border-edge px-4 py-2">{sortTabs}</div>

              <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 py-4">
                {thread$}
              </div>

              {/* Composer fixo no rodapé — é onde o frame `y6od9` o põe, e é o
                  que mantém "comentar" ao alcance do polegar com a lista
                  rolando por cima. */}
              <div className="shrink-0 border-t border-edge bg-surface px-4 py-3">
                {composer}
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  }

  /* --------------------------------------------------------------- desktop */

  return (
    <section
      data-testid="forum-section"
      aria-labelledby={headingId}
      className={cx('flex flex-col gap-4 rounded-card border border-edge bg-surface p-5', className)}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id={headingId} className="m-0 flex items-center gap-2 text-[15px] font-bold text-ink">
          <span
            aria-hidden
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] bg-accent-blue/[0.14] text-accent-blue-ink"
          >
            <MessageSquare size={18} />
          </span>
          Fórum da questão
          <span
            aria-hidden
            className="rounded-chip bg-surface-muted px-2 py-0.5 text-xs font-semibold text-ink-secondary"
          >
            {approvedCount}
          </span>
          <span className="sr-only">· {approvedCount} comentários</span>
        </h3>
        {sortTabs}
      </div>

      {/* No desktop o composer abre a seção (frame `tcgeJ`). */}
      {composer}
      {thread$}
    </section>
  );
}
